import React, { useRef, useContext, useEffect } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { RectAreaLightUniformsLib } from "./RectAreaLightUniformsLib";

import Product from "./Product";
import { ProductContext } from "../../App";

const MOBILE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

export const WIDTH_ID = "width";
export const WIDTH = 80;
export const MIN_WIDTH = 80;
export const MAX_WIDTH = 240;
export const HEIGHT_ID = "height";
export const HEIGHT = 110;
export const MIN_HEIGHT = 100;
export const MAX_HEIGHT = 180;

export const getSizeRateFor = {
  width: (width) => (width - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH),
  height: (height) => (height - MIN_HEIGHT) / (MAX_HEIGHT - MIN_HEIGHT),
};

export const getDirectionFor = { width: 0, height: 1 };

export function updateWallSize(wall, direction, sizeRate) {
  wall.children[0].morphTargetInfluences[direction] = sizeRate;
  wall.children[1].morphTargetInfluences[direction] = sizeRate;
}

export function updateBaseSize(currentProduct, direction, sizeRate) {
  currentProduct.base.children[0].children[0].morphTargetInfluences[direction] =
    sizeRate;
}

export function updateGlassSize(currentProduct, direction, sizeRate) {
  currentProduct.base.children[0].children[1].morphTargetInfluences[direction] =
    sizeRate;
}

export function updateRightWingSize(currentProduct, direction, sizeRate) {
  currentProduct.wingRight.top.children[0].morphTargetInfluences[direction] =
    sizeRate;
  currentProduct.wingRight.right.children[0].morphTargetInfluences[direction] =
    sizeRate;
  currentProduct.wingRight.bottom.children[0].morphTargetInfluences[direction] =
    sizeRate;
  currentProduct.wingRight.left.children[0].morphTargetInfluences[direction] =
    sizeRate;
}

export function updateLeftWingSize(currentProduct, direction, sizeRate) {
  currentProduct.wingLeft.top.children[0].morphTargetInfluences[direction] =
    sizeRate;
  currentProduct.wingLeft.right.children[0].morphTargetInfluences[direction] =
    sizeRate;
  currentProduct.wingLeft.bottom.children[0].morphTargetInfluences[direction] =
    sizeRate;
  currentProduct.wingLeft.left.children[0].morphTargetInfluences[direction] =
    sizeRate;
}

export function setProductSize(product, dimension, size) {
  const sizeRate = getSizeRateFor[dimension](size);
  const direction = getDirectionFor[dimension];

  updateBaseSize(product, direction, sizeRate);
  updateGlassSize(product, direction, sizeRate);
  updateRightWingSize(product, direction, sizeRate);
  updateLeftWingSize(product, direction, sizeRate);
}

export function setWallSize(wall, dimension, size) {
  const sizeRate = getSizeRateFor[dimension](size);
  const direction = getDirectionFor[dimension];

  updateWallSize(wall, direction, sizeRate);
}

function Scene(props) {
  const { setCurrentProduct, setWall } = useContext(ProductContext);
  const sceneCanvas = useRef(null);

  useEffect(() => {
    // set scene & get boundary canvas
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    let width = sceneCanvas.current.parentElement.offsetWidth;
    let height = sceneCanvas.current.parentElement.offsetHeight;
    let dimensions = { width: width, height: height };

    // // set light

    const light_amb = new THREE.AmbientLight(0xffffff, 1.0);

    const directionalLight = new THREE.DirectionalLight(0xcecece, 0.0);
    directionalLight.castShadow = true;
    directionalLight.position.set(5, 50, 5);
    directionalLight.lookAt(0, 0, 0);

    RectAreaLightUniformsLib.init();
    const light_rect = new THREE.RectAreaLight(0xdfdfdf, 0.8, 4, 3);
    light_rect.position.set(0, 4.5, 0);
    light_rect.lookAt(0, 0, 0);


    
    RectAreaLightUniformsLib.init();
    const light_rect1 = new THREE.RectAreaLight(0xffaf7a,0.5, 10, 3);
    light_rect1.position.set(-4,2, 10);
    light_rect1.lookAt(-2, 1,-5);

    RectAreaLightUniformsLib.init();
    const light_rect2 = new THREE.RectAreaLight(0xffffff,0.5, 10, 3);
    light_rect2.position.set(-4,2, 10);
    light_rect2.lookAt(-2, 1,-5);

    scene.add(light_amb);
    // scene.add(light_rect);
    scene.add(light_rect1);
    // scene.add(light_rect2);
    scene.add(directionalLight);

    // set camera
    const camera = new THREE.PerspectiveCamera(
      60,
      dimensions.width / dimensions.height,
      0.25,
      100
    );
    camera.position.z = MOBILE.test(navigator.userAgent) ? 15 : 10;

    // set renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
      canvas: sceneCanvas.current,
    });

    renderer.setSize(dimensions.width, dimensions.height);
    renderer.physicallyCorrectLights = true;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMappingExposure = 2;
    renderer.toneMapping = 3;

    // set Orbit controls

    const control = new OrbitControls(camera, renderer.domElement);
    control.target = new THREE.Vector3(0, 1.5, 0);
    control.maxPolarAngle = Math.PI * 0.495;
    control.maxDistance = 10;
    control.minDistance = 1;
    control.enableDamping = false; // an animation loop is required when either damping or auto-rotation are enabled
    control.dampingFactor = 0.05;
    control.update();

    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    new RGBELoader()
      .setPath("images/")
      .load("piece-nb-03.hdr", (streetTexture) => {
        scene.environment =
          pmremGenerator.fromEquirectangular(streetTexture).texture;

        streetTexture.dispose();
        pmremGenerator.dispose();
      });

    // autoresize canvas
    window.addEventListener("resize", function () {
      width = sceneCanvas.current.parentElement.offsetWidth;
      height = sceneCanvas.current.parentElement.offsetHeight;
      dimensions = { width: width, height: height };

      renderer.setSize(dimensions.width, dimensions.height);

      camera.aspect = dimensions.width / dimensions.height;
      camera.updateProjectionMatrix();
    });

    // add scenario
    const glLoader = new GLTFLoader();

    glLoader.load("./models/Cena_1700_shutterbox.glb", async (gltf) => {
      scene.add(gltf.scene);

      const wall = scene.getObjectByName("Wall001");
      setWallSize(wall, WIDTH_ID, WIDTH);
      setWallSize(wall, HEIGHT_ID, HEIGHT);
      setWall(wall);
    });

    // add product element by element
    glLoader.load("./models/Belgi 1700window2laneswshutters.glb", async (gltf) => {
      const elements = [];
      gltf.scene.traverse((obj) => {
        if (obj) {
          const tp = obj.type.toLowerCase();
          if ((tp === "mesh" || tp === "object3d") && "name" in obj.userData) {
            if (
              obj.userData.name.includes("Root") ||
              obj.userData.name.includes("Empty")
            ) {
              elements.push(obj.id);
            }
          }
        }
      });

      const product = new Product();

      elements.forEach((element) => {
        const obj = gltf.scene.getObjectById(element);

        switch (obj.children[0].name) {
          case "Aluminum-_Klil-Black": {
            product.base = obj;
            break;
          }
          case "left-left-v": {
            product.wingLeft.left = obj;
            break;
          }
          case "right-left-v": {
            product.wingLeft.right = obj;
            break;
          }
          case "top-left-h": {
            product.wingLeft.top = obj;
            break;
          }
          case "low-left-h": {
            product.wingLeft.bottom = obj;
            break;
          }
          case "left-right-v": {
            product.wingRight.left = obj;
            break;
          }
          case "right-right-v": {
            product.wingRight.right = obj;
            break;
          }
          case "top-right-h": {
            product.wingRight.top = obj;
            break;
          }
          case "low-right-h": {
            product.wingRight.bottom = obj;
            break;
          }
          default:
        }

        scene.add(obj);
      });

      setProductSize(product, WIDTH_ID, WIDTH);
      setProductSize(product, HEIGHT_ID, HEIGHT);

      const glass = product.base.children[0].children[1];
      glass.material = new THREE.MeshPhysicalMaterial({
        color: 0xffffffcc,
        metalness: 0.0,
        roughness: 0.0,
        thickness: 0.02,
        transmission: 1,
        side: THREE.DoubleSide,
      });


      product.setCustom(props.productConfiguration);
      product.setColor(props.productColor);

      setCurrentProduct(product);
    });

    // set loop
    const animate = function () {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
      control.update();
    };

    animate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas id="sceneCanvas" ref={sceneCanvas}></canvas>;
}

export default Scene;
